<script setup>
/**
 * The questions the REGISTRATION form asks: the terms, and a date of birth.
 *
 * Deliberately just those two, and they are the only sign-up answers left in
 * code. Where somebody plays, which country that is in and which competitions
 * they enter are asked by the SIGN-UP PROMPT SET on the first screen after this
 * one (SIGNUP_PROMPT_PLAN.md, 22 Sep 2026), authored in the console. They can
 * be, because a prompt is served to an account that already exists; these two
 * cannot, because they are answered before there is an account to serve one to.
 *
 * A sign-up form long enough to scroll is also one people abandon, and a social
 * sign-in never sees this form at all.
 *
 * Date of birth is required here because it decides who is a junior, and
 * because an under-13 must not reach an account at all.
 */
import {computed, ref, watch} from 'vue';
import TapDatePicker from '~/components/fields/TapDatePicker.vue';
import {
  SETUP_FIELD_HELP, dateOfBirthProblem, dateOfBirthWindow,
} from '~/composables/useAccountSetup';

const props = defineProps({
  disabled: {type: Boolean, default: false},
  errors: {type: Object, default: () => ({})},
});
const model = defineModel({type: Object, default: () => ({dateOfBirth: null})});

// Read once, on mount. Recomputing per render would move the window at
// midnight under someone mid-form.
const dobWindow = dateOfBirthWindow();

const dobTouched = ref(false);
const dobError = computed(() => {
  if (props.errors.dateOfBirth) return props.errors.dateOfBirth;
  if (!dobTouched.value || !model.value.dateOfBirth) return null;
  return dateOfBirthProblem(model.value.dateOfBirth);
});

// No age is refused, so the only thing a date change does here is mark the
// field touched. See MINIMUM_AGE in useAccountSetup for why.
watch(() => model.value.dateOfBirth, () => {
  dobTouched.value = true;
});
</script>

<template>
  <div class="asf">
    <!-- The picker renders its own tl-label with the date order appended, so a
         label here would print "Date of birth" twice.

         min/max/quick are NOT optional polish. The picker's defaults are the
         scheduling ones — no earlier than today, chips reading Today /
         Tomorrow / In a week — which leaves every day of a birthday disabled
         and the field impossible to answer with the calendar. -->
    <tap-date-picker
        v-model="model.dateOfBirth"
        label="Date of birth"
        birth-date
        :disabled="disabled"
        :min="dobWindow.min"
        :max="dobWindow.max"
        :quick="[]"
    />
    <div v-if="dobError" class="asf-error">{{ dobError }}</div>
    <div class="auth-help">{{ SETUP_FIELD_HELP.dateOfBirth }}</div>
  </div>
</template>

<style scoped>
.asf-error { font-size: 12.5px; color: rgb(var(--v-theme-error)); margin-top: 4px; }
</style>
